import * as THREE from 'three';
import { createOfficeRoom } from './office-room';
import {
  createDesk, createMonitor, createKeyboard,
  createWhiteboard, createPlant, createWindow, createBookshelf,
  createMug, createRug, createCoffeeMachine, createMeetingTable,
} from './furniture';
import {
  DESK_POSITIONS, WHITEBOARD_POSITION,
  COFFEE_MACHINE_POSITION, MEETING_TABLE_POSITION,
} from '@/utils/constants';

/**
 * Composes the full interior scene: room + furniture + lighting.
 * Sky-blue background. No chairs.
 */
export function createInteriorScene(): THREE.Scene {
  const scene = new THREE.Scene();
  scene.name = 'interior-scene';

  // Sky-blue background so it feels like we're in the sky
  scene.background = new THREE.Color(0x87ceeb);

  const room = createOfficeRoom();
  scene.add(room);

  // ── Primary Desk Set (desk index 0) ──────────────────────
  const deskPos0 = DESK_POSITIONS[0];
  scene.add(createDesk(deskPos0.position, deskPos0.rotation, 'desk-0'));

  // Place monitor/keyboard using rotation-aware offsets
  const dir0 = Math.cos(deskPos0.rotation); // 1 for rot=0, -1 for rot=π
  const monitorPos = deskPos0.position.clone();
  monitorPos.y = 1.025;
  monitorPos.z += dir0 * -0.2; // "behind" the desk surface relative to where worker sits
  const monitor0 = createMonitor(monitorPos, 'monitor-0');
  monitor0.rotation.y = deskPos0.rotation;
  scene.add(monitor0);

  const kbPos = deskPos0.position.clone();
  kbPos.y = 1.025;
  kbPos.z += dir0 * 0.15; // "in front" toward worker
  scene.add(createKeyboard(kbPos, 'keyboard-0'));

  const mugPos = deskPos0.position.clone();
  mugPos.y = 1.025; mugPos.x += 0.6; mugPos.z += dir0 * 0.1;
  scene.add(createMug(mugPos, 'mug-0'));

  // ── Shared furniture ─────────────────────────────────────
  scene.add(createWhiteboard(WHITEBOARD_POSITION));
  scene.add(createPlant(new THREE.Vector3(0.5, 0, -3.5), 'plant-0'));
  scene.add(createWindow(new THREE.Vector3(2, 1.5, -4.9)));
  scene.add(createBookshelf(new THREE.Vector3(5.5, 0, -2.0)));
  scene.add(createRug(new THREE.Vector3(0, 0.01, 0)));

  // ── New ambient furniture ──────────────────────────────────
  scene.add(createCoffeeMachine(COFFEE_MACHINE_POSITION));
  scene.add(createMeetingTable(MEETING_TABLE_POSITION));

  setupInteriorLighting(scene, monitorPos);

  return scene;
}

function setupInteriorLighting(scene: THREE.Scene, monitorPos: THREE.Vector3): void {
  scene.add(new THREE.AmbientLight(0xfff0e0, 0.4));

  const overhead = new THREE.PointLight(0xfff5e6, 0.8, 25);
  overhead.position.set(0, 3.3, 0);
  overhead.castShadow = true;
  overhead.shadow.mapSize.set(512, 512);
  scene.add(overhead);

  const windowLight = new THREE.DirectionalLight(0xffffff, 0.6);
  windowLight.position.set(3, 4, -1);
  windowLight.target.position.set(0, 0, 0);
  windowLight.castShadow = true;
  windowLight.shadow.mapSize.set(512, 512);
  windowLight.shadow.camera.near = 0.5;
  windowLight.shadow.camera.far = 15;
  windowLight.shadow.camera.left = -7;
  windowLight.shadow.camera.right = 7;
  windowLight.shadow.camera.top = 7;
  windowLight.shadow.camera.bottom = -7;
  scene.add(windowLight);
  scene.add(windowLight.target);

  const monitorGlow = new THREE.PointLight(0xddeeff, 0.3, 2);
  monitorGlow.position.copy(monitorPos).y += 0.3;
  monitorGlow.castShadow = false;
  scene.add(monitorGlow);

  const lampGlow = new THREE.PointLight(0xffcc88, 0.2, 1.5);
  lampGlow.position.set(monitorPos.x - 0.7, monitorPos.y + 0.2, monitorPos.z + 0.3);
  lampGlow.castShadow = false;
  scene.add(lampGlow);
}

/**
 * Add a new desk station (NO chair) at a given desk index.
 */
export function addDeskStation(scene: THREE.Scene, deskIndex: number): void {
  const deskPos = DESK_POSITIONS[deskIndex];
  if (!deskPos) return;

  scene.add(createDesk(deskPos.position, deskPos.rotation, `desk-${deskIndex}`));

  // Rotation-aware offsets so monitor always faces the worker
  const dir = Math.cos(deskPos.rotation); // 1 for rot=0, -1 for rot=π

  const mp = deskPos.position.clone();
  mp.y = 1.025;
  mp.z += dir * -0.2; // monitor behind desk center
  const mon = createMonitor(mp, `monitor-${deskIndex}`);
  mon.rotation.y = deskPos.rotation;
  scene.add(mon);

  const kp = deskPos.position.clone();
  kp.y = 1.025;
  kp.z += dir * 0.15; // keyboard in front toward worker
  scene.add(createKeyboard(kp, `keyboard-${deskIndex}`));

  const glow = new THREE.PointLight(0xddeeff, 0.3, 2);
  glow.position.set(mp.x, mp.y + 0.3, mp.z);
  glow.castShadow = false;
  glow.name = `monitor-glow-${deskIndex}`;
  scene.add(glow);
}
